import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Icon from "src/components/Icon/Icon";
import { backgroundImg } from "src/assets/assetsExport";
import PropertymarkBanner from "../../components/PropertymarkBanner/PropertymarkBanner";

const NVRE_URL = "https://nvre.co.uk";
const REDIRECT_SECONDS = 8;

const BUY_HIGHLIGHTS = [
  {
    title: "Prime London stock",
    description:
      "Apartments, townhouses, and new developments across Kensington, Chelsea, Westminster, and beyond.",
  },
  {
    title: "Expert acquisition",
    description:
      "Dedicated buying agents who negotiate on your behalf and guide you from first viewing to completion.",
  },
  {
    title: "Market intelligence",
    description:
      "Comparable sales data, area insight, and honest advice so you buy with confidence.",
  },
];

export default function BuyPage(): JSX.Element {
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCountdown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    const timeout = window.setTimeout(() => {
      window.location.href = NVRE_URL;
    }, REDIRECT_SECONDS * 1000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Buy | Letting Engine</title>
        <meta
          name="description"
          content="Buy luxury property in London through NVRE — expert acquisition, prime locations, and seamless service from the Letting Engine family."
        />
      </Helmet>

      <section className="min-h-[90vh] pt-40 pb-20 w-full relative flex items-center justify-center">
        <img
          src={backgroundImg.livingRoom3}
          alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none z-0"
        />
        <div className="absolute inset-0 bg-black/40 z-0" />

        <div className="relative z-10 text-center max-w-4xl px-6">
          <p className="font-manrope text-xs uppercase tracking-[0.25em] text-brand-yellow mb-4">
            Sales by NVRE
          </p>
          <h1 className="font-teko leading-none mb-8 tracking-[-2px] text-6xl">
            <span className="stroke-1 text-white">Buy with NVRE</span>
          </h1>
          <p className="font-manrope text-base leading-relaxed text-white max-w-2xl mx-auto mb-8">
            Property sales are handled by our sister agency, NVRE. Browse
            exclusive London homes for sale, from pied-à-terres to family
            houses, with the same premium service you expect from Letting Engine.
          </p>
          <a
            href={NVRE_URL}
            className="inline-flex items-center gap-2 bg-brand-yellow text-text-obsidian px-8 py-4 font-teko text-2xl uppercase transform skew-x-[-10deg] transition-all duration-300 hover:bg-brand-gold no-underline"
          >
            <span className="transform skew-x-[10deg] flex items-center gap-2">
              Visit nvre.co.uk
              <Icon name="arrow_forward" size={24} />
            </span>
          </a>
          <p className="font-manrope text-sm text-white/70 mt-6">
            Redirecting in {countdown} second{countdown === 1 ? "" : "s"}…
          </p>
        </div>
      </section>

      <PropertymarkBanner />

      <section className="max-w-[1440px] mx-auto px-[5%] py-20">
        <div className="border-l-4 border-solid border-brand-yellow pl-6 mb-12">
          <p className="font-manrope text-xs uppercase tracking-[0.2em] text-text-slate mb-2">
            Why buy through NVRE
          </p>
          <h2 className="font-teko text-4xl text-text-obsidian uppercase">
            London sales, expertly handled
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {BUY_HIGHLIGHTS.map((item) => (
            <div
              key={item.title}
              className="border border-solid border-[rgba(0,0,0,0.08)] p-8 bg-white shadow-soft"
            >
              <h3 className="font-teko text-2xl uppercase mb-3 text-text-obsidian">
                {item.title}
              </h3>
              <p className="font-manrope text-sm leading-relaxed text-text-slate">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="font-manrope text-base text-text-slate mb-6 max-w-2xl mx-auto">
            Letting Engine focuses on lettings. For purchases, valuations, and
            off-market opportunities, continue to our dedicated sales website.
          </p>
          <a
            href={NVRE_URL}
            className="inline-flex items-center gap-2 border-2 border-solid border-brand-yellow text-brand-yellow px-8 py-3 font-manrope font-bold uppercase tracking-[2px] text-sm transition-all duration-300 hover:bg-brand-yellow hover:text-text-obsidian no-underline"
          >
            Continue to nvre.co.uk
            <Icon name="open_in_new" size={18} />
          </a>
        </div>
      </section>
    </>
  );
}
